import type RAPIER from '@dimforge/rapier2d-compat';
import type { BumperDef, GameEvent, MovingDef, StageDefinition, Vec2 } from './types';

const FIXED_STEP = 1 / 60;
const MAX_FRAME_DELTA = .18;
const MAX_STEPS = 8;
const SEED_RADIUS = .28;
let rapierAPI: typeof RAPIER | null = null;

const GROUP = { seed: 0x0001, solid: 0x0002, sensor: 0x0004, hazard: 0x0008 } as const;
const interaction = (membership: number, filters: number): number => ((membership & 0xffff) << 16) | (filters & 0xffff);

type Entity =
  | { kind: 'seed' }
  | { kind: 'solid' }
  | { kind: 'petal'; index: number }
  | { kind: 'goal' }
  | { kind: 'thorn' }
  | { kind: 'bumper'; index: number };

export interface PhysicsSnapshot {
  seed: { position: Vec2; velocity: Vec2; rotation: number };
  moving: Vec2[];
  collectedPetals: ReadonlySet<number>;
}

export class PhysicsGarden {
  private world: RAPIER.World;
  private readonly queue: RAPIER.EventQueue;
  private seedBody: RAPIER.RigidBody;
  private readonly entities = new Map<number, Entity>();
  private readonly movingBodies: Array<{ body: RAPIER.RigidBody; definition: MovingDef }> = [];
  private readonly collectedPetals = new Set<number>();
  private readonly reportedPetals = new Set<number>();
  private accumulator = 0;
  private simulationTime = 0;
  private active = false;
  private stillTime = 0;
  private disposed = false;
  private goalRejectCooldown = 0;
  private surfaceCooldown = 0;

  static async initialize(stage: StageDefinition): Promise<PhysicsGarden> {
    if (!rapierAPI) rapierAPI = (await import('@dimforge/rapier2d-compat')).default;
    await rapierAPI.init();
    return new PhysicsGarden(stage);
  }

  private constructor(private readonly stage: StageDefinition) {
    const api = requiredAPI();
    this.world = new api.World({ x: 0, y: 9.25 });
    this.world.timestep = FIXED_STEP;
    this.queue = new api.EventQueue(true);
    this.seedBody = this.createSeed();
    this.buildStage();
  }

  private createSeed(): RAPIER.RigidBody {
    const api = requiredAPI();
    const body = this.world.createRigidBody(
      api.RigidBodyDesc.dynamic()
        .setTranslation(this.stage.spawn.x, this.stage.spawn.y)
        .setLinearDamping(.11)
        .setAngularDamping(.28)
        .setCcdEnabled(true)
        .setGravityScale(0),
    );
    const collider = this.world.createCollider(
      api.ColliderDesc.ball(SEED_RADIUS)
        .setDensity(1.05)
        .setFriction(.46)
        .setRestitution(.38)
        .setCollisionGroups(interaction(GROUP.seed, GROUP.solid | GROUP.sensor | GROUP.hazard))
        .setActiveEvents(api.ActiveEvents.COLLISION_EVENTS),
      body,
    );
    this.entities.set(collider.handle, { kind: 'seed' });
    return body;
  }

  private buildStage(): void {
    const api = requiredAPI();
    this.stage.platforms.forEach((item) => {
      const body = this.world.createRigidBody(api.RigidBodyDesc.fixed().setTranslation(item.x, item.y).setRotation(item.angle ?? 0));
      const collider = this.world.createCollider(api.ColliderDesc.cuboid(item.width / 2, item.height / 2).setFriction(.74).setRestitution(.15).setCollisionGroups(interaction(GROUP.solid, GROUP.seed)), body);
      this.entities.set(collider.handle, { kind: 'solid' });
    });

    this.stage.moving.forEach((item) => {
      const body = this.world.createRigidBody(api.RigidBodyDesc.kinematicPositionBased().setTranslation(item.x, item.y).setRotation(item.angle ?? 0));
      const collider = this.world.createCollider(api.ColliderDesc.cuboid(item.width / 2, item.height / 2).setFriction(.68).setRestitution(.24).setCollisionGroups(interaction(GROUP.solid, GROUP.seed)), body);
      this.entities.set(collider.handle, { kind: 'solid' });
      this.movingBodies.push({ body, definition: item });
    });

    this.stage.petals.forEach((item, index) => {
      const body = this.world.createRigidBody(api.RigidBodyDesc.fixed().setTranslation(item.x, item.y));
      const collider = this.world.createCollider(
        api.ColliderDesc.ball(item.radius * .76).setSensor(true).setCollisionGroups(interaction(GROUP.sensor, GROUP.seed)).setActiveEvents(api.ActiveEvents.COLLISION_EVENTS), body,
      );
      this.entities.set(collider.handle, { kind: 'petal', index });
    });

    this.stage.thorns.forEach((item) => {
      const body = this.world.createRigidBody(api.RigidBodyDesc.fixed().setTranslation(item.x, item.y).setRotation(item.angle ?? 0));
      const collider = this.world.createCollider(
        api.ColliderDesc.cuboid(item.width / 2, item.height / 2).setSensor(true).setCollisionGroups(interaction(GROUP.hazard, GROUP.seed)).setActiveEvents(api.ActiveEvents.COLLISION_EVENTS), body,
      );
      this.entities.set(collider.handle, { kind: 'thorn' });
    });

    this.stage.bumpers.forEach((item, index) => {
      const body = this.world.createRigidBody(api.RigidBodyDesc.fixed().setTranslation(item.x, item.y));
      const collider = this.world.createCollider(
        api.ColliderDesc.ball(item.radius).setFriction(.08).setRestitution(.82).setCollisionGroups(interaction(GROUP.solid, GROUP.seed)).setActiveEvents(api.ActiveEvents.COLLISION_EVENTS), body,
      );
      this.entities.set(collider.handle, { kind: 'bumper', index });
    });

    const goalBody = this.world.createRigidBody(api.RigidBodyDesc.fixed().setTranslation(this.stage.goal.x, this.stage.goal.y));
    const goalCollider = this.world.createCollider(
      api.ColliderDesc.ball(this.stage.goal.radius * .64).setSensor(true).setCollisionGroups(interaction(GROUP.sensor, GROUP.seed)).setActiveEvents(api.ActiveEvents.COLLISION_EVENTS), goalBody,
    );
    this.entities.set(goalCollider.handle, { kind: 'goal' });
  }

  launch(velocity: Vec2): void {
    if (this.disposed) return;
    this.seedBody.setGravityScale(1, true);
    this.seedBody.setLinvel(velocity, true);
    this.seedBody.setAngvel(velocity.x * .3, true);
    this.active = true;
    this.stillTime = 0;
  }

  pulse(direction: Vec2, strength = 4.25): void {
    if (!this.active || this.disposed) return;
    const length = Math.hypot(direction.x, direction.y) || 1;
    const mass = this.seedBody.mass();
    this.seedBody.applyImpulse({ x: direction.x / length * strength * mass, y: direction.y / length * strength * mass }, true);
  }

  setReadyPosition(position: Vec2): void {
    if (this.active || this.disposed) return;
    this.seedBody.setTranslation(position, true);
    this.seedBody.setLinvel({ x: 0, y: 0 }, true);
  }

  advance(frameDelta: number, onEvent: (event: GameEvent) => void): number {
    if (!this.active || this.disposed) return 0;
    const accepted = Math.min(MAX_FRAME_DELTA, Math.max(0, frameDelta));
    this.accumulator = Math.min(MAX_FRAME_DELTA, this.accumulator + accepted);
    let steps = 0;
    while (this.accumulator >= FIXED_STEP && steps < MAX_STEPS) {
      this.fixedStep(onEvent);
      this.accumulator -= FIXED_STEP;
      steps += 1;
    }
    if (steps === MAX_STEPS) this.accumulator = 0;
    return steps;
  }

  private fixedStep(onEvent: (event: GameEvent) => void): void {
    this.simulationTime += FIXED_STEP;
    this.goalRejectCooldown = Math.max(0, this.goalRejectCooldown - FIXED_STEP);
    this.surfaceCooldown = Math.max(0, this.surfaceCooldown - FIXED_STEP);
    this.updateMovingBodies();
    this.applyWind();
    this.world.step(this.queue);
    this.queue.drainCollisionEvents((first, second, started) => {
      if (!started) return;
      const a = this.entities.get(first);
      const b = this.entities.get(second);
      if (!a || !b) return;
      const other = a.kind === 'seed' ? b : b.kind === 'seed' ? a : null;
      if (other) this.handleSeedCollision(other, onEvent);
    });
    this.checkBoundsAndMotion(onEvent);
  }

  private updateMovingBodies(): void {
    for (const item of this.movingBodies) {
      const wave = Math.sin(this.simulationTime * item.definition.speed + (item.definition.phase ?? 0));
      item.body.setNextKinematicTranslation({
        x: item.definition.x + (item.definition.axis === 'x' ? wave * item.definition.distance : 0),
        y: item.definition.y + (item.definition.axis === 'y' ? wave * item.definition.distance : 0),
      });
    }
  }

  private applyWind(): void {
    const position = this.seedBody.translation();
    for (const wind of this.stage.winds) {
      if (Math.abs(position.x - wind.x) <= wind.width / 2 && Math.abs(position.y - wind.y) <= wind.height / 2) {
        const mass = this.seedBody.mass();
        this.seedBody.addForce({ x: wind.force.x * mass, y: wind.force.y * mass }, true);
      }
    }
  }

  private handleSeedCollision(entity: Entity, onEvent: (event: GameEvent) => void): void {
    const position = this.position;
    if (entity.kind === 'petal' && !this.collectedPetals.has(entity.index)) {
      this.collectedPetals.add(entity.index);
      return;
    }
    if (entity.kind === 'solid' && this.surfaceCooldown <= 0) {
      const speed = this.speed;
      if (speed > 1.8) {
        this.surfaceCooldown = .12;
        onEvent({ type: 'surface', position, intensity: Math.min(1, speed / 11) });
      }
      return;
    }
    if (entity.kind === 'thorn') {
      this.active = false;
      onEvent({ type: 'fail', position, cause: 'thorn' });
      return;
    }
    if (entity.kind === 'bumper') {
      const bumper: BumperDef | undefined = this.stage.bumpers[entity.index];
      if (!bumper) return;
      const dx = position.x - bumper.x;
      const dy = position.y - bumper.y;
      const length = Math.hypot(dx, dy) || 1;
      const speed = this.speed;
      const impulse = Math.min(bumper.strength * .58, 1.15 + speed * .18);
      const mass = this.seedBody.mass();
      this.seedBody.applyImpulse({ x: dx / length * impulse * mass, y: dy / length * impulse * mass }, true);
      onEvent({ type: 'bumper', position, intensity: Math.min(1, speed / 10) });
      return;
    }
    if (entity.kind === 'goal' && this.goalRejectCooldown <= 0) {
      const speed = this.speed;
      if (this.collectedPetals.size < this.stage.requiredPetals) {
        this.goalRejectCooldown = .75;
        const dx = position.x - this.stage.goal.x;
        const dy = position.y - this.stage.goal.y;
        const length = Math.hypot(dx, dy) || 1;
        const mass = this.seedBody.mass();
        this.seedBody.applyImpulse({ x: dx / length * 2.1 * mass, y: (dy / length * 2.1 - .5) * mass }, true);
        onEvent({ type: 'goal-locked', position, remaining: this.stage.requiredPetals - this.collectedPetals.size });
      } else if (speed <= this.stage.goal.maxSpeed) {
        this.active = false;
        onEvent({ type: 'complete', position, perfect: speed < this.stage.goal.maxSpeed * .5 });
      } else {
        this.goalRejectCooldown = .75;
        const dx = position.x - this.stage.goal.x;
        const dy = position.y - this.stage.goal.y;
        const length = Math.hypot(dx, dy) || 1;
        const mass = this.seedBody.mass();
        this.seedBody.applyImpulse({ x: dx / length * 2.4 * mass, y: (dy / length * 2.4 - .6) * mass }, true);
        onEvent({ type: 'goal-rejected', position, speed });
      }
    }
  }

  private checkBoundsAndMotion(onEvent: (event: GameEvent) => void): void {
    const position = this.position;
    if (position.x < -1.3 || position.x > this.stage.world.width + 1.3 || position.y > this.stage.world.height + 2 || position.y < -4) {
      this.active = false;
      onEvent({ type: 'fail', position, cause: 'out-of-bounds' });
      return;
    }
    const velocity = this.seedBody.linvel();
    this.stillTime = Math.hypot(velocity.x, velocity.y) < .13 ? this.stillTime + FIXED_STEP : 0;
    if (this.stillTime > 2.2) {
      this.active = false;
      onEvent({ type: 'fail', position, cause: 'stalled' });
    }
  }

  consumeCollectedPetals(): number[] {
    const pending: number[] = [];
    for (const index of this.collectedPetals) {
      if (!this.reportedPetals.has(index)) {
        this.reportedPetals.add(index);
        pending.push(index);
      }
    }
    return pending;
  }

  get snapshot(): PhysicsSnapshot {
    const translation = this.seedBody.translation();
    const velocity = this.seedBody.linvel();
    return {
      seed: { position: { x: translation.x, y: translation.y }, velocity: { x: velocity.x, y: velocity.y }, rotation: this.seedBody.rotation() },
      moving: this.movingBodies.map(({ body }) => ({ x: body.translation().x, y: body.translation().y })),
      collectedPetals: this.collectedPetals,
    };
  }

  get position(): Vec2 { const p = this.seedBody.translation(); return { x: p.x, y: p.y }; }
  get velocity(): Vec2 { const v = this.seedBody.linvel(); return { x: v.x, y: v.y }; }
  get speed(): number { const v = this.seedBody.linvel(); return Math.hypot(v.x, v.y); }
  get isActive(): boolean { return this.active; }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.queue.free();
    this.world.free();
    this.entities.clear();
    this.movingBodies.length = 0;
    this.collectedPetals.clear();
    this.reportedPetals.clear();
  }
}

export const PHYSICS_CONSTANTS = { fixedStep: FIXED_STEP, maxFrameDelta: MAX_FRAME_DELTA, maxSteps: MAX_STEPS, seedRadius: SEED_RADIUS } as const;

function requiredAPI(): typeof RAPIER {
  if (!rapierAPI) throw new Error('Rapier must initialize before a physics garden is constructed.');
  return rapierAPI;
}
